import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { docsGuideLink, trackHelpLinkClicked } from '@/components/layout/HelpMenu';

/**
 * The "Testing Your AI Service" guide (`project/docs/user/innovate-testing-tab.md`)
 * by its docs-site slug (task 1193). The one place the slug is written: the
 * Testing tab's rail link and each section's Learn more build from it.
 */
export const TESTING_GUIDE_SLUG = 'innovate-testing-tab';

/** The guide headings each section's Learn more opens at (docs-site heading ids). */
export const TESTING_GUIDE_ANCHORS = {
  tests: 'generating-scenarios',
  criteria: 'criteria-the-rules-every-answer-is-graded-against',
  runs: 'what-a-run-needs-to-pass',
} as const;

export type TestingGuideAnchor = keyof typeof TESTING_GUIDE_ANCHORS;

/** `help_link_clicked` destination for every link to the guide. */
const TESTING_GUIDE_DESTINATION = 'testing_guide';

/** The guide's URL from the configured docs base, optionally at one section's heading. */
export function testingGuideLink(anchor?: TestingGuideAnchor): string {
  const href = docsGuideLink(TESTING_GUIDE_SLUG);
  return anchor ? `${href}#${TESTING_GUIDE_ANCHORS[anchor]}` : href;
}

const trackClick = () => trackHelpLinkClicked(TESTING_GUIDE_DESTINATION);

/** `help_link_clicked` location for the rail-header ? (task 1195). */
const RAIL_HEADER_LOCATION = 'testing_rail_header';

const HOW_TESTING_WORKS = 'How testing works';

/**
 * The always-visible "How testing works" ? on the Testing tab's section rail
 * heading row (task 1195; sections layout only), for a User who does not yet
 * know which section to open. Icon only: the accessible name carries the words.
 */
export function HowTestingWorksLink() {
  return (
    <a
      href={testingGuideLink()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackHelpLinkClicked(TESTING_GUIDE_DESTINATION, RAIL_HEADER_LOCATION)}
      data-testid="testing-how-it-works"
      aria-label={HOW_TESTING_WORKS}
      title={HOW_TESTING_WORKS}
      className="inline-flex items-center justify-center rounded-full text-gray-400 hover:text-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-500 dark:hover:text-indigo-400"
    >
      <QuestionMarkCircleIcon className="h-4 w-4" aria-hidden="true" />
    </a>
  );
}

/** A trailing "Learn more" after a section's explanatory sentence, opening the guide at that section. */
export function TestingGuideLearnMore({ anchor, testId }: { anchor: TestingGuideAnchor; testId: string }) {
  return (
    <a
      href={testingGuideLink(anchor)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={trackClick}
      data-testid={testId}
      className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
    >
      Learn more
    </a>
  );
}
